import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { VOLUNTEER_CATEGORIES } from '../constants/volunteerCategories';
import { useVolunteerOpportunities } from '../hooks/useVolunteerOpportunities';
import { useVolunteerCategories } from '../hooks/useVolunteerCategories';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const AddButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary);
  color: #fff;
  padding: 0.55rem 0.9rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  box-shadow: 0 10px 18px rgba(31, 117, 254, 0.25);
`;

export const VolunteerPage: React.FC = () => {
  const { data, isLoading, error } = useVolunteerOpportunities();
  const { data: volunteerCategories } = useVolunteerCategories();
  const categories =
    volunteerCategories && volunteerCategories.length > 0
      ? volunteerCategories
          .filter((cat) => cat.is_active)
          .map((cat) => ({ id: cat.key, label: cat.label }))
      : VOLUNTEER_CATEGORIES;
  const categoryLabelById = React.useMemo(() => {
    return new Map(categories.map((category) => [category.id, category.label]));
  }, [categories]);

  if (isLoading) return <p>Loading opportunities…</p>;
  if (error) return <p>Error loading opportunities</p>;

  return (
    <Page>
      <HeaderRow>
        <HeaderTitle>Volunteer Opportunities</HeaderTitle>
        <AddButton to="/volunteers/new">+ Add Opportunity</AddButton>
      </HeaderRow>
      <TableCard>
        <TableWrap>
          <Table>
            <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Location</th>
              <th>Time Commitment</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data?.map((o) => (
              <tr key={o.id}>
                <td>{o.title}</td>
                <td>{o.category ? categoryLabelById.get(o.category) ?? o.category : '—'}</td>
                <td>{o.location ?? '—'}</td>
                <td>{o.time_commitment ?? '—'}</td>
                <td>{o.created_at ? new Date(o.created_at).toLocaleDateString() : '—'}</td>
                <td>
                  <Link to={`/volunteers/${o.id}`}>Edit</Link>
                </td>
              </tr>
            ))}
          </tbody>
          </Table>
        </TableWrap>
      </TableCard>
    </Page>
  );
};

export default VolunteerPage;
